import React, { useEffect, useMemo, useState } from 'react';
import { FieldGroupAttrs, FieldTab, Layout, UNPLACED_TAB, groupFields, sectionKey } from '../utils/fieldGroups';

// 页签 → 分组 → 字段 的嵌套布局 (参数页与模板编辑器共用).
// 结构完全取模板布局 layout (见 utils/fieldGroups); 无布局时单段平铺, 与原来一致.

interface Props<T> {
    items: T[];
    attrs: (item: T) => FieldGroupAttrs;
    /** 模板布局 (页签 → 分组); null = 未启用分组, 平铺 */
    layout: Layout;
    /** 没有字段的分组也显示 (编辑器要能往空分组里加字段; 参数页不显示空分组) */
    showEmpty?: boolean;
    itemKey: (item: T) => React.Key;
    renderItem: (item: T) => React.ReactNode;
    /** 每段字段的容器 class (参数页 4 列网格 / 编辑器单列) */
    gridClassName: string;
    /** 分组标题右侧的附加操作 (编辑器: 改名 / 在此添加) */
    sectionExtra?: (tab: string, sec: string) => React.ReactNode;
    /** 页签栏下方、分组列表上方的内容 (编辑器: + 新分组), 参数为当前页签 */
    belowTabs?: (tab: string) => React.ReactNode;
    /** 当前页签变化回调 (编辑器"添加字段"要加到当前页签) */
    onTabChange?: (tab: string) => void;
    /** 外部指定要切到的页签 (如新加字段后跳过去); 变化时生效 */
    focusTab?: string;
    /** 页签栏附加 class (编辑器里吸顶) */
    tabBarClassName?: string;
    /** 分组可折叠 (点分组标题整行切换) */
    collapsible?: boolean;
    /** 初始全部折叠 (编辑器字段多, 先看结构) */
    defaultCollapsed?: boolean;
    /** 外部要求展开某分组 (如刚在里面加了字段); 传新对象即触发, key 用 sectionKey(tab, sec) */
    openSection?: { key: string };
}

/** 用非表单元素做可点击项: 参数页只读态外层是 <fieldset disabled>, 里面的 <button> 会被一并禁用 */
function clickProps(onActivate: () => void) {
    return {
        tabIndex: 0,
        onClick: onActivate,
        onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onActivate();
            }
        },
    };
}

function GroupedFields<T>({
    items,
    attrs,
    layout,
    showEmpty = false,
    itemKey,
    renderItem,
    gridClassName,
    sectionExtra,
    belowTabs,
    onTabChange,
    focusTab,
    tabBarClassName = '',
    collapsible = false,
    defaultCollapsed = false,
    openSection,
}: Props<T>) {
    const tabs: FieldTab<T>[] = useMemo(() => {
        const all = groupFields(items, attrs, layout);
        if (showEmpty) return all;
        // 参数页: 空分组 / 空页签不占位 (字段联动隐藏后也会出现整段为空)
        return all
            .map((t) => ({ ...t, sections: t.sections.filter((s) => s.items.length > 0) }))
            .filter((t) => t.sections.length > 0);
    }, [items, attrs, layout, showEmpty]);
    const [active, setActive] = useState('');
    useEffect(() => {
        if (focusTab !== undefined && tabs.some((t) => t.tab === focusTab)) setActive(focusTab);
    }, [focusTab, tabs]);
    // 当前页签被删空 / 改名后回落到第一个
    const cur = tabs.find((t) => t.tab === active) || tabs[0];
    const curTab = cur?.tab;
    useEffect(() => {
        if (curTab !== undefined && onTabChange) onTabChange(curTab);
    }, [curTab, onTabChange]);
    // 折叠态 = 默认态 (openByDefault) 取反 flipped 里的分组
    const openByDefault = !defaultCollapsed;
    const [flipped, setFlipped] = useState<Set<string>>(new Set());
    const isOpen = (key: string) => !collapsible || openByDefault !== flipped.has(key);
    const toggle = (key: string) =>
        setFlipped((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    useEffect(() => {
        if (!openSection) return;
        const { key } = openSection;
        setFlipped((prev) => {
            const next = new Set(prev);
            if (openByDefault) next.delete(key);
            else next.add(key);
            return next;
        });
        // 只响应新的 openSection 请求
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openSection]);
    if (!cur) return null;

    return (
        <div>
            {tabs.length > 1 || cur.tab ? (
                <div className={`flex items-center border-b border-slate-200 mb-3 ${tabBarClassName}`}>
                <div role="tablist" className="flex items-center flex-1 min-w-0 overflow-x-auto">
                    {tabs.map((t) => (
                        <span
                            key={t.tab}
                            role="tab"
                            aria-selected={t.tab === cur.tab}
                            {...clickProps(() => setActive(t.tab))}
                            className={`px-4 py-2 text-sm border-b-2 -mb-px whitespace-nowrap cursor-pointer select-none ${
                                t.tab === cur.tab
                                    ? 'border-brand-600 text-brand-600 font-medium'
                                    : 'border-transparent text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            {t.tab || '全部'}
                            <span className="ml-1 text-[11px] text-slate-400">
                                {t.sections.reduce((n, s) => n + s.items.length, 0)}
                            </span>
                        </span>
                    ))}
                </div>
                </div>
            ) : null}
            {belowTabs && belowTabs(cur.tab)}
            <div className="space-y-4">
                {cur.sections.map((s) => {
                    const key = sectionKey(cur.tab, s.sec);
                    const hasHeader = !!s.sec;
                    const open = !hasHeader || isOpen(key);
                    return (
                        <div key={s.sec || '__nosec'}>
                            {hasHeader && (
                                // 整行 (含空白处) 点击即折叠/展开; 右侧操作按钮阻止冒泡, 不会误触
                                <div
                                    role={collapsible ? 'button' : undefined}
                                    aria-expanded={collapsible ? open : undefined}
                                    className={`flex items-center gap-2 mb-2 py-1 border-b border-slate-100 select-none ${
                                        collapsible ? 'cursor-pointer hover:bg-slate-50 rounded-sm' : ''
                                    }`}
                                    {...(collapsible ? clickProps(() => toggle(key)) : {})}
                                >
                                    <span className="flex items-center gap-1.5">
                                        {collapsible && (
                                            <span className="text-[10px] text-slate-400 w-3">{open ? '▾' : '▸'}</span>
                                        )}
                                        <span className="text-xs font-semibold text-slate-600">
                                            {s.sec || <span className="text-slate-400 font-normal">{UNPLACED_TAB}</span>}
                                        </span>
                                        <span className="text-[11px] text-slate-300">{s.items.length}</span>
                                    </span>
                                    {sectionExtra && (
                                        <div
                                            className="ml-auto cursor-default"
                                            onClick={(e) => e.stopPropagation()}
                                            onKeyDown={(e) => e.stopPropagation()}
                                        >
                                            {sectionExtra(cur.tab, s.sec)}
                                        </div>
                                    )}
                                </div>
                            )}
                            {open && s.items.length === 0 && showEmpty && (
                                <div className="text-[11px] text-slate-400 px-1 pb-1">暂无字段</div>
                            )}
                            {open && s.items.length > 0 && (
                                <div className={gridClassName}>
                                    {s.items.map((it) => (
                                        <React.Fragment key={itemKey(it)}>{renderItem(it)}</React.Fragment>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default GroupedFields;
